const CHINA_TIME_ZONE = 'Asia/Shanghai';

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: CHINA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function localDateKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  const parts = dateFormatter.formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));
  return `${values.year}-${values.month}-${values.day}`;
}

function dateKeyBefore(dateKey, days) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day - days));
  return date.toISOString().slice(0, 10);
}

function chinaDayStart(dateKey) {
  return new Date(`${dateKey}T00:00:00+08:00`).getTime();
}

function blankDay(date) {
  return {
    date,
    formulaMl: 0,
    breastmilkMl: 0,
    nursingMinutes: 0,
    feedSessions: 0,
    poopCount: 0,
    peeCount: 0,
    sleepMinutes: 0,
    sleepSessions: 0,
  };
}

export function buildStats(events, days = 1, now = new Date()) {
  const safeDays = [1, 7, 30].includes(Number(days)) ? Number(days) : 1;
  const today = localDateKey(now);
  const startDate = dateKeyBefore(today, safeDays - 1);
  const endExclusiveDate = dateKeyBefore(today, -1);
  const rangeStartMs = chinaDayStart(startDate);
  const rangeEndMs = chinaDayStart(endExclusiveDate);
  const daily = Array.from({ length: safeDays }, (_, index) => blankDay(dateKeyBefore(today, safeDays - 1 - index)));
  const dailyByDate = new Map(daily.map((day) => [day.date, day]));
  const sleepMillisecondsByDate = new Map(daily.map((day) => [day.date, 0]));

  const result = {
    rangeDays: safeDays,
    startDate,
    endDate: today,
    hasData: false,
    feed: { formulaMl: 0, breastmilkMl: 0, nursingMinutes: 0, sessions: 0 },
    diaper: { poopCount: 0, peeCount: 0, poopLevels: [0, 0, 0, 0, 0], peeLevels: [0, 0, 0] },
    sleep: { minutes: 0, sessions: 0, activeSessions: 0 },
    daily,
  };

  for (const event of events ?? []) {
    if (event.kind === 'sleep') {
      const sleepStartMs = new Date(event.startedAt).getTime();
      const sleepEndMs = event.endedAt ? new Date(event.endedAt).getTime() : Math.min(new Date(now).getTime(), rangeEndMs);
      if (!Number.isFinite(sleepStartMs) || !Number.isFinite(sleepEndMs) || sleepEndMs <= sleepStartMs) continue;

      const overlapStartMs = Math.max(sleepStartMs, rangeStartMs);
      const overlapEndMs = Math.min(sleepEndMs, rangeEndMs);
      if (overlapEndMs <= overlapStartMs) continue;
      result.hasData = true;

      if (!event.endedAt) {
        result.sleep.activeSessions += 1;
        continue;
      }

      result.sleep.sessions += 1;
      let cursorDate = localDateKey(overlapStartMs);
      while (cursorDate <= today) {
        const dayStartMs = chinaDayStart(cursorDate);
        const nextDayMs = chinaDayStart(dateKeyBefore(cursorDate, -1));
        const segmentStartMs = Math.max(overlapStartMs, dayStartMs);
        const segmentEndMs = Math.min(overlapEndMs, nextDayMs);
        if (segmentEndMs > segmentStartMs) {
          sleepMillisecondsByDate.set(cursorDate, (sleepMillisecondsByDate.get(cursorDate) ?? 0) + segmentEndMs - segmentStartMs);
        }
        if (nextDayMs >= overlapEndMs) break;
        cursorDate = dateKeyBefore(cursorDate, -1);
      }
      const sessionStartDate = localDateKey(event.startedAt);
      const firstVisibleDate = localDateKey(overlapStartMs);
      const sessionDay = dailyByDate.has(sessionStartDate) ? sessionStartDate : firstVisibleDate;
      if (dailyByDate.has(sessionDay)) dailyByDate.get(sessionDay).sleepSessions += 1;
      continue;
    }

    const key = localDateKey(event.startedAt);
    const day = dailyByDate.get(key);
    if (!day) continue;
    result.hasData = true;

    if (event.kind === 'feed') {
      result.feed.sessions += 1;
      day.feedSessions += 1;
      if (event.subtype === 'formula') {
        result.feed.formulaMl += event.amount;
        day.formulaMl += event.amount;
      } else if (event.subtype === 'breastmilk') {
        result.feed.breastmilkMl += event.amount;
        day.breastmilkMl += event.amount;
      } else if (event.subtype === 'nursing') {
        result.feed.nursingMinutes += event.amount;
        day.nursingMinutes += event.amount;
      }
    }
    if (event.kind === 'poop') {
      result.diaper.poopCount += 1;
      day.poopCount += 1;
      if (event.amount >= 1 && event.amount <= 5) result.diaper.poopLevels[event.amount - 1] += 1;
    }
    if (event.kind === 'pee') {
      result.diaper.peeCount += 1;
      day.peeCount += 1;
      if (event.amount >= 1 && event.amount <= 3) result.diaper.peeLevels[event.amount - 1] += 1;
    }
  }

  for (const day of daily) {
    day.sleepMinutes = Math.round((sleepMillisecondsByDate.get(day.date) ?? 0) / 60000);
    result.sleep.minutes += day.sleepMinutes;
  }

  return result;
}

export { localDateKey };
